import type { ReactNode } from 'react';
import classNames from 'classnames';
import { Column, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { OutlineArrowRightIcon } from '@tempmee/react-icons';
import './ShiftCard.scss';

export interface IShiftCardProps {
  title: string;
  /** Numeric metric shown in the headline slot. Omit when passing `icon`. */
  count?: number;
  /**
   * Illustration rendered in place of the count for non-metric tiles (e.g. the
   * Best-in-Dental quick-apply tile). An icon tile is always treated as active
   * so its CTA and accent bar show.
   */
  icon?: ReactNode;
  barColor: string;
  ctaText?: string;
  isClickable: boolean;
  onClick?: () => void;
  countColor?: string;
  isLoading?: boolean;
  showCtaWhenEmpty?: boolean;
  emptyStateMessage?: string;
  dataCy?: string;
  countDataCy?: string;
}


export const ShiftCard = ({
  title,
  count,
  icon,
  barColor,
  ctaText,
  isClickable,
  onClick,
  countColor,
  isLoading,
  showCtaWhenEmpty = false,
  emptyStateMessage,
  dataCy,
  countDataCy,
}: IShiftCardProps) => {
  const isActive = !isLoading && (icon != null || (count ?? 0) > 0);
  const showBar = isClickable && (isActive || showCtaWhenEmpty);
  const shouldShowCta = isClickable && ctaText && (isActive || showCtaWhenEmpty);
  const showEmptyMessage = !isActive && !isLoading && emptyStateMessage;

  const cardClassName = classNames('shift-card', {
    'shift-card--clickable': isClickable,
    'shift-card--not-clickable': !isClickable,
    'shift-card--active': isActive || shouldShowCta,
  });

  return (
    <div onClick={isClickable ? onClick : undefined} className={cardClassName} data-cy={dataCy}>
      {isLoading ? (
        <div className="shift-card__skeleton">
          <div className="shift-card__skeleton-count" />
          <div className="shift-card__skeleton-title" />
        </div>
      ) : (
        <>
          <Column align="start">
            {icon != null ? (
              icon
            ) : (
              <Typography
                variant="h3"
                color={isActive ? countColor || colors.neutral[600] : colors.neutral[200]}
                data-cy={countDataCy}
              >
                {count}
              </Typography>
            )}

            <Typography variant="h6">{title}</Typography>
          </Column>

          {shouldShowCta && (
            <Row align="center" gap={3} justify="end">
              <OutlineArrowRightIcon className="shift-card__arrow" color={barColor} />
              <Typography variant="body1">
                {ctaText}
              </Typography>
            </Row>
          )}

          {showBar && <div className="shift-card__bar" style={{ backgroundColor: barColor }} />}
          {showEmptyMessage && (
            <div className="shift-card__empty-message" style={{ backgroundColor: barColor }}>
              <Typography variant="body1" color={colors.shades['0']}>
                {emptyStateMessage}
              </Typography>
            </div>
          )}
        </>
      )}
    </div>
  );
};
